import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CircleNotch,
  MagnifyingGlass,
  X,
  FunnelSimple,
  Receipt,
  Trash,
} from "@phosphor-icons/react";
import InvoiceModal from "@/components/InvoiceModal";
import { ListHeader, ListToolbar, FilterPanel, FilterSelect, FilterDate } from "@/components/ListPage";
import { useProperty } from "@/contexts/PropertyContext";

const TYPE_LABELS = {
  racun: "Račun",
  avans: "Avans",
  ponuda: "Ponuda",
  storno: "Storno",
};

/**
 * Global list of every invoice ever issued (or in draft).
 * Filters: type, status, paid state, date range, free-text search on client/number.
 */
export default function InvoicesPage() {
  const { activePropertyId, properties } = useProperty();
  const [rows, setRows] = useState([]);
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 768);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  // filter state
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [paid, setPaid] = useState("");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [propertyFilter, setPropertyFilter] = useState("");
  const [sort, setSort] = useState(() => localStorage.getItem("rw_invoices_sort") || "date_desc");

  // The global Property Switcher (in the TopBar) takes precedence over the
  // local per-page filter. When the switcher is set to a single property,
  // hide the local property dropdown to avoid the confusion of two filters.
  const effectivePropertyId = activePropertyId || propertyFilter;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (type) params.set("invoice_type", type);
      if (status) params.set("status", status);
      if (paid) params.set("paid", paid);
      if (q) params.set("search", q);
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      if (effectivePropertyId) params.set("property_id", effectivePropertyId);
      params.set("sort", sort);
      const { data } = await api.get(`/invoices?${params.toString()}`);
      setRows(data || []);
    } catch (e) {
      toast.error("Greška pri učitavanju");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [effectivePropertyId]);

  useEffect(() => {
    load();
  }, [type, status, paid, from, to, sort]);

  useEffect(() => { localStorage.setItem("rw_invoices_sort", sort); }, [sort]);

  const totals = useMemo(() => {
    const t = { count: rows.length, paid: 0, unpaid: 0, sum: 0 };
    rows.forEach((r) => {
      if (r.paid) t.paid += 1; else if (!r.cancelled) t.unpaid += 1;
      if (!r.cancelled) t.sum += Number(r.total || 0);
    });
    return t;
  }, [rows]);

  const togglePaid = async (inv, next) => {
    try {
      await api.put(`/invoices/${inv.id}`, { paid: next });
      setRows((prev) => prev.map((r) => (r.id === inv.id ? { ...r, paid: next } : r)));
    } catch (e) {
      toast.error("Greška");
    }
  };

  const deleteInvoice = async (inv) => {
    const label = inv.status === "draft" ? "draft" : "račun";
    if (!window.confirm(`Obrisati ${label} ${inv.number}? Ova akcija se ne može poništiti.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      setRows((prev) => prev.filter((r) => r.id !== inv.id));
      toast.success("Obrisano");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Greška pri brisanju");
    }
  };

  const clearFilters = () => {
    setType(""); setStatus(""); setPaid(""); setQ(""); setFrom(""); setTo("");
  };

  return (
    <div className="rw-page" data-testid="invoices-page">
      <ListHeader
        title="Računi"
        summary={<>{totals.count} dokumenata · {totals.unpaid} neplaćeno · <span className="font-semibold text-foreground">{totals.sum.toFixed(2)} EUR</span></>}
      />
      <ListToolbar
        value={q}
        onChange={setQ}
        onSubmit={load}
        placeholder="Traži klijenta ili broj..."
        filtersOpen={showFilters}
        onToggleFilters={() => setShowFilters((v) => !v)}
        filterCount={[type, status, paid, from, to, propertyFilter].filter(Boolean).length}
        testIdPrefix="inv"
      >
        <div className="hidden w-40 shrink-0 sm:block">
          <FilterSelect value={sort} onChange={setSort} testId="inv-sort" options={[
            { value: "date_desc", label: "Najnoviji prvo" },
            { value: "date_asc", label: "Najstariji prvo" },
            { value: "amount_desc", label: "Iznos: veći prvo" },
            { value: "amount_asc", label: "Iznos: manji prvo" },
          ]} />
        </div>
      </ListToolbar>
      <FilterPanel open={showFilters} onReset={() => { clearFilters(); setPropertyFilter(""); }} onApply={load} canReset={!!(type || status || paid || from || to || propertyFilter)} testIdPrefix="inv">
        <div className="sm:hidden">
          <FilterSelect value={sort} onChange={setSort} testId="inv-sort-mobile" options={[
            { value: "date_desc", label: "Najnoviji prvo" },
            { value: "date_asc", label: "Najstariji prvo" },
            { value: "amount_desc", label: "Iznos: veći prvo" },
            { value: "amount_asc", label: "Iznos: manji prvo" },
          ]} />
        </div>
        <FilterSelect value={type} onChange={setType} testId="inv-filter-type" options={[
          { value: "", label: "Svi tipovi" }, { value: "racun", label: "Račun" }, { value: "avans", label: "Avans" }, { value: "ponuda", label: "Ponuda" }, { value: "storno", label: "Storno" },
        ]} />
        <FilterSelect value={status} onChange={setStatus} testId="inv-filter-status" options={[
          { value: "", label: "Svi statusi" }, { value: "draft", label: "U pripremi" }, { value: "issued", label: "Zaključen" },
        ]} />
        <FilterSelect value={paid} onChange={setPaid} testId="inv-filter-paid" options={[
          { value: "", label: "Plaćeni i neplaćeni" }, { value: "true", label: "Samo plaćeni" }, { value: "false", label: "Samo neplaćeni" },
        ]} />
        {!activePropertyId && properties.length > 1 && (
          <FilterSelect value={propertyFilter} onChange={setPropertyFilter} testId="inv-filter-property" options={[
            { value: "", label: "Svi smještaji" }, ...properties.map((p) => ({ value: p.id, label: p.name })),
          ]} />
        )}
        <FilterDate value={from} onChange={(v) => { setFrom(v); if (to && to < v) setTo(v); }} placeholder="Od datuma" testId="inv-filter-from" />
        <FilterDate value={to} onChange={setTo} minDate={from || undefined} placeholder="Do datuma" testId="inv-filter-to" />
      </FilterPanel>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <CircleNotch size={22} className="animate-spin text-primary" />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-lg border border-dashed border-border">
          <Receipt size={56} weight="thin" className="text-muted-foreground/40" />
          <p className="mt-3 text-sm text-muted-foreground">Nema računa za odabrane filtere</p>
        </div>
      ) : (
        <>
        {/* Desktop/tablet table */}
        <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 text-left font-semibold">Datum</th>
                <th className="px-3 py-2 text-left font-semibold">Broj</th>
                <th className="px-3 py-2 text-left font-semibold">Tip</th>
                <th className="px-3 py-2 text-left font-semibold">Klijent</th>
                <th className="px-3 py-2 text-right font-semibold">Iznos</th>
                <th className="px-3 py-2 text-center font-semibold">Račun plaćen</th>
                <th className="px-3 py-2 text-center font-semibold">Status</th>
                <th className="px-3 py-2 text-center font-semibold">Akcije</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => setOpenId(inv.id)}
                  className={`cursor-pointer transition hover:bg-muted/30 ${inv.cancelled ? "opacity-50" : ""}`}
                  data-testid={`inv-list-row-${inv.id}`}
                >
                  <td className="px-3 py-2.5">{fmtDate(inv.issue_date)}</td>
                  <td className="px-3 py-2.5 font-mono font-semibold">
                    {inv.status === "draft" ? (
                      <span className="rounded bg-amber-50 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-700">Priprema</span>
                    ) : inv.number}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
                      inv.invoice_type === "storno" ? "bg-rose-50 text-rose-700"
                        : inv.invoice_type === "ponuda" ? "bg-sky-50 text-sky-700"
                        : inv.invoice_type === "avans" ? "bg-violet-50 text-violet-700"
                        : "bg-emerald-50 text-emerald-700"
                    }`}>
                      {inv.cancelled ? "Storniran" : TYPE_LABELS[inv.invoice_type]}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">{inv.client_name}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold">
                    {Number(inv.total || 0).toFixed(2)} {inv.currency}
                  </td>
                  <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => togglePaid(inv, !inv.paid)}
                      disabled={inv.cancelled || inv.status === "draft"}
                      className={`relative inline-block h-5 w-9 rounded-full transition ${inv.paid ? "bg-emerald-500" : "bg-slate-300"} ${inv.cancelled || inv.status === "draft" ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid={`inv-list-paid-${inv.id}`}
                    >
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${inv.paid ? "left-4" : "left-0.5"}`} />
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {inv.status === "issued" ? (
                      <span className="rounded bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Zaključen</span>
                    ) : (
                      <span className="rounded bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Priprema</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                    {(inv.status === "draft" || inv.cancelled) && (
                      <button
                        onClick={() => deleteInvoice(inv)}
                        className="rounded p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"
                        title={inv.cancelled ? "Obriši stornirani račun" : "Obriši draft"}
                        data-testid={`inv-list-delete-${inv.id}`}
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card view */}
        <div className="space-y-2 md:hidden" data-testid="invoices-mobile-cards">
          {rows.map((inv) => (
            <div
              key={inv.id}
              onClick={() => setOpenId(inv.id)}
              className={`flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-card px-3 py-3 transition active:scale-[0.99] ${inv.cancelled ? "opacity-50" : ""}`}
              data-testid={`inv-card-${inv.id}`}
            >
              <button
                onClick={(e) => { e.stopPropagation(); togglePaid(inv, !inv.paid); }}
                disabled={inv.cancelled || inv.status === "draft"}
                title={inv.paid ? "Plaćen" : "Neplaćen"}
                className={`relative inline-block h-5 w-9 shrink-0 rounded-full transition ${inv.paid ? "bg-emerald-500" : "bg-muted-foreground/30"} ${inv.cancelled || inv.status === "draft" ? "cursor-not-allowed opacity-40" : ""}`}
                data-testid={`inv-card-paid-${inv.id}`}
              >
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${inv.paid ? "left-4" : "left-0.5"}`} />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{inv.client_name || "—"}</p>
                  <span className="shrink-0 font-mono text-sm font-semibold text-foreground">{Number(inv.total || 0).toFixed(2)} {inv.currency}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="min-w-0 flex-1 truncate font-mono">{inv.status === "draft" ? "Priprema" : inv.number} · {fmtDate(inv.issue_date)}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    inv.cancelled || inv.invoice_type === "storno" ? "bg-rose-500/10 text-rose-600"
                      : inv.invoice_type === "ponuda" ? "bg-sky-500/10 text-sky-600"
                      : inv.invoice_type === "avans" ? "bg-violet-500/10 text-violet-600"
                      : "bg-emerald-500/10 text-emerald-600"
                  }`}>{inv.cancelled ? "Storniran" : TYPE_LABELS[inv.invoice_type]}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {openId && (
        <InvoiceModal
          invoiceId={openId}
          onClose={() => { setOpenId(null); load(); }}
          onSaved={load}
        />
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso || "";
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}
